import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Sparkles, ArrowRight, Map as MapIcon } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Active ambassadors strip — pulled live from `/api/showcase/embajadores`.
 * Shared, editorial-style social-proof section used on both the homepage
 * (Landing.js) and the ambassador program page (Ambassadors.js).
 */
const ActiveEmbajadoresSection = () => {
  const [embajadores, setEmbajadores] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/showcase/embajadores?limit=12`)
      .then((r) => setEmbajadores(r.data?.items || []))
      .catch(() => setEmbajadores([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || embajadores.length === 0) return null;

  return (
    <section
      className="bg-[#F9F8F6] py-24 md:py-32 border-y border-stone-200/50"
      data-testid="active-embajadores-section"
    >
      <div className="mx-auto max-w-6xl px-6">
        <div className="mb-16 text-center max-w-2xl mx-auto">
          <p className="text-teal-700 text-sm font-semibold uppercase tracking-[0.2em] mb-4">
            Embajadores activos
          </p>
          <h2 className="font-display text-5xl md:text-6xl text-stone-900 tracking-tight leading-[1.05] mb-4">
            Ya están publicando.
          </h2>
          <p className="text-stone-600 text-lg leading-relaxed">
            Viajeros reales compartiendo sus itinerarios en{' '}
            <Link to="/rutas" className="text-teal-700 font-semibold hover:underline">
              Itinerarios
            </Link>
            . Cada ruta se puede clonar y adaptar en segundos.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
          {embajadores.map((c) => {
            const avatar = c.avatar_id
              ? `${BACKEND_URL}/api/files/${c.avatar_id}`
              : c.picture
              ? (c.picture.startsWith('http') ? c.picture : `${BACKEND_URL}${c.picture}`)
              : null;
            return (
              <Link
                key={c.user_id}
                to={`/u/${c.username}`}
                data-testid={`embajador-card-${c.username}`}
                className="group relative bg-white rounded-[2rem] p-8 md:p-10 border border-stone-200/60 shadow-sm transition-transform transition-shadow duration-300 ease-out hover:-translate-y-2 hover:shadow-xl hover:shadow-stone-200/50 flex flex-col items-center text-center"
              >
                <div className="relative w-28 h-28 md:w-32 md:h-32 mb-5">
                  {avatar ? (
                    <img
                      src={avatar}
                      alt={`Avatar de ${c.username}`}
                      className="w-full h-full rounded-full object-cover border-4 border-white shadow-md"
                    />
                  ) : (
                    <div className="w-full h-full rounded-full bg-gradient-to-br from-teal-400 via-emerald-500 to-cyan-600 flex items-center justify-center text-white text-3xl font-display border-4 border-white shadow-md">
                      {(c.name || c.username || '?').charAt(0).toUpperCase()}
                    </div>
                  )}
                  <span className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-amber-400 ring-4 ring-white flex items-center justify-center shadow-sm">
                    <Sparkles className="w-4 h-4 text-amber-900" />
                  </span>
                </div>

                <h3 className="font-display text-2xl text-stone-900 mt-1 mb-1 group-hover:text-teal-700 transition-colors">
                  {c.name || c.username}
                </h3>
                <p className="text-teal-600 font-medium text-sm mb-6">@{c.username}</p>

                <div className="flex items-center gap-5">
                  <div className="flex flex-col items-center">
                    <span className="font-display text-2xl font-semibold text-stone-900">{c.routes_count}</span>
                    <span className="text-[10px] text-stone-500 uppercase tracking-widest font-bold mt-0.5">
                      Itinerario{c.routes_count === 1 ? '' : 's'}
                    </span>
                  </div>
                </div>

                <span className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-stone-400 group-hover:text-teal-700 transition-colors">
                  <MapIcon className="w-3.5 h-3.5" />
                  Ver su ruta
                </span>
              </Link>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <Link
            to="/rutas"
            className="inline-flex items-center gap-2 rounded-full bg-stone-900 hover:bg-teal-800 text-white px-7 py-3.5 text-sm font-semibold shadow-md transition-colors duration-300"
            data-testid="view-all-routes-btn"
          >
            Ver todos los itinerarios <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default ActiveEmbajadoresSection;
